import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { notifyLogoUpdate } from '../components/DynamicLogo';
import { FaUpload, FaTrash, FaImage, FaCheckCircle } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { getActiveToken, getActiveAuthRole } from '../utils/authStorage';

const API_URL = process.env.REACT_APP_REGIT_SERVICE_URL || '';

export default function Configuration() {
  const navigate = useNavigate();
  const [logo, setLogo] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // Charger le logo et les couleurs existantes
  useEffect(() => {
    if (getActiveAuthRole() === 'merchant') {
      navigate('/dashboard', { replace: true });
      return;
    }
    const token = getActiveToken();
    if (!token) {
      setError('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    fetchLogoData();
  }, []);

  const fetchLogoData = async () => {
    try {
      const token = getActiveToken();
      if (!token) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 5000
      };

      const response = await axios.get(`${API_URL}/logo`, config);

      if (response.data && !response.data.success && response.data.message === 'Logo non trouvé') {
        setLogo(null);
        return;
      }

      if (response.data.success) {
        setLogo(response.data.logo);

        // Tester immédiatement si le fichier du logo est accessible
        if (response.data.logo.gridfsId) {
          try {
            const testImage = await axios.get(`${API_URL}/logo/file`, {
              ...config,
              responseType: 'blob'
            });
            console.log('Logo file accessible:', testImage.status === 200);
          } catch (imgErr) {
            console.warn('Logo file not accessible:', imgErr.message);
          }
        }

        // Log les données reçues pour le débogage
        console.log('Données du logo reçues:', response.data);
      } else {
        console.warn('Réponse reçue mais pas de succès:', response.data);
        setError('Format de réponse invalide');
      }
    } catch (err) {
      console.error('Erreur détaillée lors du chargement du logo:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: err.config
      });

      // Gestion spécifique des erreurs selon l'API
      if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else if (err.response?.status === 404) {
        if (err.response?.data?.message === 'Logo non trouvé') {
          // C'est un cas normal pour une première configuration
          console.log('Pas de logo configuré - Configuration initiale');
          setLogo(null);
          
          setError('');  // Efface les erreurs précédentes
          return;
        } else {
          setError('API de logo non accessible. Vérifiez la configuration du serveur.');
        }
      } else if (err.response?.status === 500 && err.response?.data?.message === 'GridFS non initialisé') {
        setError('Erreur de configuration du serveur (GridFS). Contactez l\'administrateur.');
      } else if (err.code === 'ECONNABORTED') {
        setError('Le serveur met trop de temps à répondre. Réessayez plus tard.');
      } else if (err.code === 'ECONNREFUSED') {
        setError('Impossible de se connecter au serveur. Vérifiez que le serveur est en cours d\'exécution.');
      } else {
        setError(`Erreur: ${err.response?.data?.message || err.message || 'Erreur inconnue'}`);
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorChange = (colorKey, value) => {
    setColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Vérifier si le token est présent
      const token = getActiveToken();
      if (!token) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      const formData = new FormData();
      
      // Ajouter le fichier logo s'il y en a un nouveau
      if (fileInputRef.current?.files[0]) {
        const file = fileInputRef.current.files[0];
        // Vérification de la taille du fichier (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('Le fichier est trop volumineux. Taille maximum : 5MB');
        }
        formData.append('logo', file);
      } else {
        throw new Error('Veuillez sélectionner un fichier');
      }

      // Envoi avec gestion du timeout
      const response = await axios.put(`${API_URL}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        timeout: 30000, // 30 secondes de timeout
      });

      if (response.data.success) {
        // Notifier tous les composants DynamicLogo de la mise à jour
        notifyLogoUpdate();
        
        // Forcer le rafraîchissement des images
        const timestamp = new Date().getTime();
        document.querySelectorAll('img[src*="/logo/file"]').forEach(img => {
          img.src = `${API_URL}/logo/file?t=${timestamp}`;
        });
        
        await fetchLogoData();
        setPreviewImage(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        
        // Message de succès
        setSuccess('Configuration mise à jour avec succès');
        setTimeout(() => setSuccess(''), 3000);

        // Forcer le rafraîchissement du cache du navigateur
        const cacheUrls = [
          `${API_URL}/logo/${response.data.logo.gridfsId}`,
          `${API_URL}/logo`
        ];

        // Invalider le cache pour ces URLs
        cacheUrls.forEach(url => {
          fetch(url, { 
            method: 'GET',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          }).catch(() => {});
        });
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError(err.message || 'Erreur lors de la mise à jour des paramètres');
    } finally {
      setLoading(false);
    }
  };

  // Open a confirmation modal instead of native window.confirm
  const handleDelete = () => setConfirmOpen(true);

  // Perform the actual delete request (called when user confirms in modal)
  const performDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      const token = getActiveToken();
      if (!token) {
        setError('Session expirée. Veuillez vous reconnecter.');
        setDeleting(false);
        setConfirmOpen(false);
        return;
      }

      const response = await axios.delete(`${API_URL}/logo`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      if (response.data?.success) {
        setLogo(null);
        setPreviewImage(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSuccess('Logo supprimé avec succès');
        setTimeout(() => setSuccess(''), 3000);
        setConfirmOpen(false);
      } else {
        setError(response.data?.message || 'Erreur lors de la suppression du logo');
      }
    } catch (err) {
      console.error('Erreur lors de la suppression du logo:', err);
      if (err.response) {
        const status = err.response.status;
        if (status === 401) setError('Session expirée. Veuillez vous reconnecter.');
        else if (status === 404) setError(err.response.data?.message || 'Logo non trouvé');
        else setError(err.response.data?.message || 'Erreur serveur lors de la suppression du logo');
      } else if (err.code === 'ECONNABORTED') {
        setError('Le serveur met trop de temps à répondre. Réessayez.');
      } else {
        setError(err.message || 'Erreur lors de la suppression du logo');
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar />
        <div className="flex-1 overflow-x-hidden overflow-y-auto">
          <div className="container mx-auto px-6 py-8 max-w-4xl">
            {/* En-tête avec icône */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <FaImage className="text-white text-lg" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900">Configuration</h1>
              </div>
              <p className="text-slate-600 ml-13">Personnalisez l'identité visuelle de votre application</p>
            </div>
      
            {/* Messages d'alerte avec animations */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 shadow-sm animate-slideIn">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="flex-1">{error}</span>
                </div>
              </div>
            )}
            {success && (
              <div className="bg-emerald-50 border-l-4 border-emerald-500 text-emerald-700 p-4 rounded-lg mb-6 shadow-sm animate-slideIn">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="flex-1">{success}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Section Logo avec design amélioré */}
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                    <FaImage className="text-blue-600 text-sm" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900">Logo</h2>
                </div>
                
                <div className="space-y-6">
                  {/* Aperçu du logo avec design moderne */}
                  <div className="flex flex-col md:flex-row items-start gap-8">
                    <div className="relative group">
                      <div className="w-40 h-40 border-2 border-dashed border-slate-300 rounded-2xl flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 to-white group-hover:border-slate-400 transition-colors duration-300">
                        {(previewImage || (logo && logo.gridfsId)) ? (
                          <img
                            src={previewImage || `${API_URL}/logo/file?t=${new Date().getTime()}`}
                            alt="Logo preview"
                            className="max-w-full max-h-full object-contain p-4"
                          />
                        ) : (
                          <div className="text-center">
                            <FaImage className="mx-auto text-4xl text-slate-300 mb-2" />
                            <span className="text-sm text-slate-400">Aucun logo</span>
                          </div>
                        )}
                      </div>
                      {(previewImage || (logo && logo.gridfsId)) && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-4">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/*"
                        className="hidden"
                        id="logo-upload"
                      />
                      
                      <div className="space-y-3">
                        <label
                          htmlFor="logo-upload"
                          className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-slate-100 to-slate-50 hover:from-slate-200 hover:to-slate-100 text-slate-700 font-medium rounded-xl cursor-pointer transition-all duration-300 shadow-sm hover:shadow-md border border-slate-200"
                        >
                          <FaUpload className="text-lg" />
                          <span>Choisir un fichier</span>
                        </label>
                        
                        {(logo || previewImage) && (
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 text-red-600 font-medium rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-red-200"
                          >
                            <FaTrash className="text-lg" />
                            <span>Supprimer le logo</span>
                          </button>
                        )}
                      </div>
                      
                     
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton de soumission avec design moderne */}
              <div className="flex items-center justify-between pt-2">
                
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Enregistrement...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FaCheckCircle />
                      Enregistrer le logo
                    </span>
                  )}
                </button>
              </div>
            </form>

            {/* Modal de confirmation moderne */}
            {confirmOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
                <div 
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
                  onClick={() => setConfirmOpen(false)} 
                />
                <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 animate-scaleIn">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FaTrash className="text-red-600 text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">Confirmer la suppression</h3>
                      <p className="text-sm text-slate-600">
                        Êtes-vous sûr de vouloir supprimer le logo ? Cette action est irréversible et le logo sera définitivement supprimé.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 mt-6">
                    <button 
                      onClick={() => setConfirmOpen(false)} 
                      className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors duration-200"
                    >
                      Annuler
                    </button>
                    <button 
                      onClick={performDelete} 
                      disabled={deleting} 
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Suppression...
                        </>
                      ) : (
                        <>
                          <FaTrash />
                          Supprimer
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Ajoutez ces styles CSS dans votre fichier global ou dans un module CSS
// @keyframes slideIn {
//   from { transform: translateY(-10px); opacity: 0; }
//   to { transform: translateY(0); opacity: 1; }
// }
// @keyframes fadeIn {
//   from { opacity: 0; }
//   to { opacity: 1; }
// }
// @keyframes scaleIn {
//   from { transform: scale(0.95); opacity: 0; }
//   to { transform: scale(1); opacity: 1; }
// }
// .animate-slideIn { animation: slideIn 0.3s ease-out; }
// .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
// .animate-scaleIn { animation: scaleIn 0.3s ease-out; }